// @ts-nocheck
import {
  Component,
  Input,
  Output,
  EventEmitter,
  HostListener,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';

export interface NavigationItem {
  label: string;
  link: string;
  active?: boolean;
  pageType?: string; // Optional pageType for click handler
}

export interface Logo {
  image?: string;
  alt?: string;
  text?: string;
  link?: string;
}

export interface CTAButton {
  text: string;
  link?: string;
  backgroundColor?: string;
  textColor?: string;
}

/**
 * Page header component for minimal website templates; displays logo, navigation, CTA button and manages header interactions including mobile menu behavior and click handling.
 * @component
 * @selector app-page-header
 * @example
 *   <app-page-header
 *     [logo]="siteLogo"
 *     [navigation]="navItems"
 *     [ctaButton]="primaryCta"
 *     [visible]="true"
 *     [clickable]="true"
 *     (headerClick)="onHeaderClicked()"
 *     (navigationClick)="onNavigate($event)">
 *   </app-page-header>
 * @input {Logo | null} logo - Logo object used in the header (may be null).
 * @input {NavigationItem[]} navigation - Array of navigation items rendered in the header.
 * @input {CTAButton | null} ctaButton - Optional call-to-action button configuration.
 * @input {boolean} visible - Controls whether the header is rendered/visible.
 * @input {boolean} clickable - When true the header is interactive; when false it behaves as preview mode.
 * @input {boolean} selected - Visual selected state for the header (used by builder/editor).
 * @input {string | null} headingColor - Optional branding color for the heading; fallback is used if not provided.
 * @input {string | null} headingFont - Optional heading font from branding; fallback is used if not provided.
 * @input {string | null} backgroundColor - Optional background color for the header.
 * @output {EventEmitter<void>} headerClick - Emitted when the header (outside of nav/CTA/menu) is clicked and clickable is true.
 * @output {EventEmitter<string>} navigationClick - Emitted with a resolved pageType (string) when a navigation item or CTA is clicked.
 *
 * Key methods:
 * - onHeaderClick(event: Event): Handles top-level header clicks, ignores clicks inside nav/CTA/menu elements and emits headerClick when appropriate.
 * - onNavigationItemClick(item: NavigationItem, event: Event): Prevents duplicate clicks, sets local active state for immediate visual feedback, resolves a pageType from item.pageType or item.link (normalizing hashes, paths and query params), emits navigationClick, closes mobile menu and debounces navigation state.
 * - onCTAButtonClick(event: Event): Normalizes CTA link to a pageType (handles hash links, path patterns and matching navigation items), emits navigationClick and closes mobile menu.
 * - isNavigationItemActive(item: NavigationItem, index: number): Returns whether a navigation item should be considered active, preferring local active index when set.
 * - toggleMobileMenu(event: Event): Toggles mobile menu open state; always enabled to allow editor/preview interaction.
 * - closeMobileMenu(): Closes the mobile menu.
 * - getHeadingColor(): Returns the configured heading color or a sensible default.
 * - getHeadingFont(): Returns the configured heading font or a sensible default.
 *
 * Important lifecycle hooks and behaviors:
 * - ngOnChanges(changes: SimpleChanges): Observes navigation input changes to initialize or update the local active navigation index (prefers incoming active flag, otherwise defaults to first item when unset).
 * - Host listeners:
 *   - document:click => onDocumentClick(event: Event): Closes mobile menu when clicking outside the drawer or hamburger button.
 *   - document:keydown.escape => onEscapeKey(): Closes mobile menu on Escape key.
 *
 * Internal state notes:
 * - isMobileMenuOpen: Boolean tracking mobile menu visibility.
 * - isNavigating: Internal flag used to prevent duplicate navigation clicks (reset after short timeout).
 * - activeNavigationIndex: Locally tracked active index for immediate UI feedback separate from the input model.
 *
 * Additional notes:
 * - The component normalizes links and maps common path/hash patterns (e.g., express-washes, express-details, fleets, coupons) to canonical pageType strings for navigation events.
 * - Host classes 'clickable' and 'preview-mode' are toggled based on the clickable input to alter styling/behavior in editor vs. preview.
 */
@Component({
  selector: 'app-page-header',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './page-header.component.html',
  styleUrls: ['./page-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    '[class.clickable]': 'clickable',
    '[class.preview-mode]': '!clickable',
    '[class.is-tablet]': "deviceType === 'tablet'",
    '[class.is-mobile]': "deviceType === 'mobile'",
  },
})
export class PageHeaderComponent implements OnChanges {
  @Input() logo: Logo | null = null;
  @Input() navigation: NavigationItem[] = [];
  @Input() ctaButton: CTAButton | null = null;
  @Input() visible = true;
  @Input() clickable = false;
  @Input() selected = false;
  @Input() headingColor: string | null = null; // Heading color from branding
  @Input() headingFont: string | null = null; // Heading font from branding
  @Input() backgroundColor: string | null = null; // Background color for header
  @Input() deviceType: 'desktop' | 'tablet' | 'mobile' = 'desktop';
  @Output() headerClick = new EventEmitter<void>();
  @Output() navigationClick = new EventEmitter<string>();

  // Mobile menu state
  isMobileMenuOpen = false;

  // Prevent duplicate clicks
  private isNavigating = false;

  // Track active navigation item locally for immediate visual feedback
  private activeNavigationIndex: number | null = null;

  /**
   * Handle a click on the page header: if the component is clickable and the click target is not inside navigation or menu elements, emit the headerClick event.
   * @example
   * nHeaderClick(event)
   * undefined
   * @param {{Event}} {{event}} - Click event object whose target is inspected to determine if the headerClick should be emitted.
   * @returns {{void}} Nothing is returned.
   **/
  onHeaderClick(event: Event): void {
    if (this.clickable) {
      // Don't propagate if clicking on navigation items or menu button
      const target = event.target as HTMLElement;
      if (
        target.closest('.nav-link') ||
        target.closest('.book-wash-btn') ||
        target.closest('.hamburger-menu') ||
        target.closest('.mobile-menu-overlay') ||
        target.closest('.mobile-menu-drawer')
      ) {
        return;
      }
      this.headerClick.emit();
    }
  }

  /**
   * Handles a navigation item click: prevents default behavior, determines the target pageType, updates active state, emits a navigationClick event, and closes the mobile menu.
   * @example
   * nNavigationItemClick(item, event)
   * undefined
   * @param {NavigationItem} item - The navigation item that was clicked.
   * @param {Event} event - The DOM event for the click; default and propagation will be prevented.
   * @returns {void} No return value.
   **/
  onNavigationItemClick(item: NavigationItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    // Prevent duplicate clicks
    if (this.isNavigating) {
      return;
    }
    this.isNavigating = true;

    // Update active state immediately for visual feedback
    const clickedIndex = this.navigation.findIndex(nav => nav === item);
    if (clickedIndex !== -1) {
      this.activeNavigationIndex = clickedIndex;
      // Also update the item's active property for immediate visual update
      this.navigation.forEach((nav, index) => {
        nav.active = index === clickedIndex;
      });
    }

    // Use pageType if available (preferred method)
    let pageType = item.pageType;

    if (!pageType && item.link) {
      // Remove hash, leading/trailing slashes and normalize
      const normalizedLink = item.link
        .replace(/^#+|\/+$/g, '')
        .replace(/^\/+/, '')
        .toLowerCase();

      // Map common link patterns to pageType
      if (normalizedLink === '' || normalizedLink === 'home' || normalizedLink === '/') {
        pageType = 'home';
      } else if (
        normalizedLink === 'expresswashes' ||
        normalizedLink === 'express-washes' ||
        normalizedLink.includes('express-wash')
      ) {
        pageType = 'express-wash-packages';
      } else if (
        normalizedLink === 'expressdetails' ||
        normalizedLink === 'express-details' ||
        normalizedLink.includes('express-detail') ||
        normalizedLink.includes('express-auto')
      ) {
        pageType = 'express-auto-details';
      } else if (
        normalizedLink === 'fleets' ||
        normalizedLink === 'fleet' ||
        normalizedLink.includes('fleet')
      ) {
        pageType = 'fleet-management';
      } else if (
        normalizedLink === 'coupons' ||
        normalizedLink === 'coupon' ||
        normalizedLink.includes('coupon')
      ) {
        pageType = 'coupons';
      } else {
        // Try to extract pageType from query parameters: /preview?websiteId=...&pageType=...
        const urlParams = new URLSearchParams(item.link.split('?')[1] || '');
        pageType = urlParams.get('pageType') || undefined;

        // If still not found, try to extract from path: /website-preview/{templateId}/{pageType}
        if (!pageType) {
          const match = item.link.match(/\/website-preview\/[^/]+(?:\/(.+))?$/);
          if (match) {
            pageType = match[1] || 'home';
          } else {
            // Fallback: use the normalized link as pageType
            pageType = normalizedLink || 'home';
          }
        }
      }
    }

    // Default to home if still no pageType found
    if (!pageType) {
      pageType = 'home';
    }

    this.navigationClick.emit(pageType);
    // Close mobile menu when navigation item is clicked
    this.isMobileMenuOpen = false;

    // Reset navigation flag after a short delay
    setTimeout(() => {
      this.isNavigating = false;
    }, 300);
  }

  // Check if a navigation item is active
  isNavigationItemActive(item: NavigationItem, index: number): boolean {
    // Use local active state if set, otherwise fall back to item's active property
    if (this.activeNavigationIndex !== null) {
      return index === this.activeNavigationIndex;
    }
    return item.active === true;
  }

  /**
   * Handle CTA button click: normalize the CTA link, determine a page type, emit a navigation event, and close the mobile menu.
   * @example
   * nCTAButtonClick(new Event('click'))
   * undefined
   * @param {Event} event - Click event from the CTA button.
   * @returns {void} No return value; emits navigationClick with the resolved page type and closes the mobile menu.
   **/
  onCTAButtonClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.ctaButton?.link) return;

    // Convert link to page type - handle both hash links (#expresswashes) and regular links
    const link = this.ctaButton.link;
    // Remove hash, leading/trailing slashes and normalize
    const normalizedLink = link
      .replace(/^#+|\/+$/g, '')
      .replace(/^\/+/, '')
      .toLowerCase();

    let pageType = 'home';
    if (normalizedLink && normalizedLink !== 'home' && normalizedLink !== '') {
      // Handle hash links like #expresswashes, #expressdetails, #fleets, #coupons
      if (
        normalizedLink === 'expresswashes' ||
        normalizedLink.includes('express-wash') ||
        normalizedLink === 'express-washes'
      ) {
        pageType = 'express-wash-packages';
      } else if (
        normalizedLink === 'expressdetails' ||
        normalizedLink.includes('express-detail') ||
        normalizedLink.includes('express-auto') ||
        normalizedLink === 'express-details'
      ) {
        pageType = 'express-auto-details';
      } else if (normalizedLink === 'fleets' || normalizedLink.includes('fleet')) {
        pageType = 'fleet-management';
      } else if (normalizedLink === 'coupons' || normalizedLink.includes('coupon')) {
        pageType = 'coupons';
      } else {
        // Try to match with navigation items
        const matchingNav = this.navigation.find(nav => {
          const navLink = (nav.link || '')
            .replace(/^#+|\/+$/g, '')
            .replace(/^\/+/, '')
            .toLowerCase();
          return navLink === normalizedLink || navLink.replace(/-/g, '') === normalizedLink;
        });
        if (matchingNav && matchingNav.pageType) {
          pageType = matchingNav.pageType;
        }
      }
    }

    this.navigationClick.emit(pageType);
    // Close mobile menu when CTA button is clicked
    this.isMobileMenuOpen = false;
  }

  toggleMobileMenu(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    // Always allow menu to toggle - it should work in both editor and preview mode
    // The mobile menu overlay and drawer are only shown when clickable is false (preview mode)
    // but the hamburger button should always be functional
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
  }

  // Close mobile menu when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    // Close menu if clicking outside the menu and hamburger button
    if (
      this.isMobileMenuOpen &&
      !target.closest('.mobile-menu-drawer') &&
      !target.closest('.hamburger-menu')
    ) {
      this.closeMobileMenu();
    }
  }

  // Close mobile menu on escape key
  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.isMobileMenuOpen) {
      this.closeMobileMenu();
    }
  }

  /**
   * Update component's activeNavigationIndex when the navigation input changes.
   * @example
   * gOnChanges({ navigation: [{ label: 'Home', active: true }] })
   * void
   * @param {{SimpleChanges}} {{changes}} - Object describing which @Input properties have changed.
   * @returns {{void}} No return value.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    // Update active state when navigation input changes
    if (changes['navigation'] && this.navigation && this.navigation.length > 0) {
      // Find the active item from the input
      const activeIndex = this.navigation.findIndex(nav => nav.active === true);
      if (activeIndex !== -1) {
        this.activeNavigationIndex = activeIndex;
      } else if (this.activeNavigationIndex === null) {
        // If no active item is set and we haven't set one locally, default to first item
        this.activeNavigationIndex = 0;
      }
    }
  }

  /**
   * Get heading color from branding or use default
   */
  getHeadingColor(): string {
    return this.headingColor || '#15172E';
  }

  /**
   * Get heading font from branding or use default
   */
  getHeadingFont(): string {
    return this.headingFont || 'Inter';
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
